// Configuration i18next (fr/en, défaut anglais, vouvoiement en FR).
// Détection initiale : locale du navigateur → fr si fr-*, sinon en. La ressource
// est bundlée (pas de fetch runtime) — parité des clés garantie par scripts/check-i18n.ts.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enAccessNs from './locales/en/access.json';
import enAccount from './locales/en/account.json';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enDiscovery from './locales/en/discovery.json';
import enInteractions from './locales/en/interactions.json';
import enLanding from './locales/en/landing.json';
import enLibrary from './locales/en/library.json';
import enNetwork from './locales/en/network.json';
import enNotifications from './locales/en/notifications.json';
import enRedeem from './locales/en/redeem.json';
import frAccessNs from './locales/fr/access.json';
import frAccount from './locales/fr/account.json';
import frAuth from './locales/fr/auth.json';
import frCommon from './locales/fr/common.json';
import frDiscovery from './locales/fr/discovery.json';
import frInteractions from './locales/fr/interactions.json';
import frLanding from './locales/fr/landing.json';
import frLibrary from './locales/fr/library.json';
import frNetwork from './locales/fr/network.json';
import frNotifications from './locales/fr/notifications.json';
import frRedeem from './locales/fr/redeem.json';

export const SUPPORTED_LOCALES = ['fr', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
// Français par défaut POUR TOUT LE MONDE (décision produit). L'anglais reste
// disponible via le sélecteur de langue, et un choix explicite est persisté.
export const DEFAULT_LOCALE: Locale = 'fr';
const LOCALE_STORAGE_KEY = 'remember.locale';

// Mémorise le choix explicite de langue (respecté aux prochains chargements).
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Stockage indisponible (mode privé strict) — sans effet, on retombe sur fr.
  }
}
export const DEFAULT_NS = 'common';
export const NAMESPACES = ['common', 'auth', 'landing', 'library', 'interactions', 'network', 'account', 'discovery', 'access', 'redeem', 'notifications'] as const;

export const resources = {
  fr: {
    common: frCommon,
    auth: frAuth,
    landing: frLanding,
    library: frLibrary,
    interactions: frInteractions,
    network: frNetwork,
    account: frAccount,
    discovery: frDiscovery,
    access: frAccessNs,
    redeem: frRedeem,
    notifications: frNotifications,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    landing: enLanding,
    library: enLibrary,
    interactions: enInteractions,
    network: enNetwork,
    account: enAccount,
    discovery: enDiscovery,
    access: enAccessNs,
    redeem: enRedeem,
    notifications: enNotifications,
  },
} as const;

// Locale initiale : le choix explicite persisté s'il existe, sinon FRANÇAIS pour
// tout le monde (on n'auto-détecte plus la langue du navigateur — décision produit).
export function detectLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === 'fr' || stored === 'en') return stored;
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE;
}

let initialized = false;

export function initI18n(locale?: Locale): typeof i18n {
  if (!initialized) {
    void i18n.use(initReactI18next).init({
      resources,
      lng: locale ?? DEFAULT_LOCALE,
      fallbackLng: DEFAULT_LOCALE,
      defaultNS: DEFAULT_NS,
      ns: [...NAMESPACES],
      interpolation: { escapeValue: false }, // React échappe déjà
      returnNull: false,
    });
    initialized = true;
  }
  return i18n;
}

export default i18n;
